/**
 * The payment step's standard look (the prototype's Payment section and
 * submit): the app's card panel, Payments secured by Stripe, the decline line,
 * and Confirm & pay; the app's states in this look, with the app's words
 * (deposits, card paused, too close to pay with Pick a new time, already paid,
 * confirming and a slow confirmation with Done). Draws the behavior half's state
 * and calls its actions.
 */
import { CardCheckoutPanel } from '@/components/payment/CardCheckoutPanel';
import { CardPaymentWait } from '@/components/payment/cardPaymentWait';
import { CHECKOUT_CONFIRM_WORDS } from '@/components/payment/CheckoutOverlay';

import { partsOf, pieceRoot } from '../../contract/runtime';
import shared from '../../styles/shared.module.css';
import { Icon } from '../../ui/Icon';
import type { PaymentStepRuntime } from './usePaymentStep';
import css from './PaymentStep.module.css';
import { contract } from './contract';

const part = partsOf(contract);

export function PaymentStepView({ state, actions, panel }: PaymentStepRuntime) {
  if (state.phase === 'confirming' || state.phase === 'slowConfirm') {
    const slow = state.phase === 'slowConfirm';
    return (
      <div className={css.confirming} role="status" aria-live="polite" {...pieceRoot(contract)} {...part('confirming')}>
        <p className={css.confirmTitle}>{CHECKOUT_CONFIRM_WORDS.title}</p>
        <p>{`${state.amount} paid via card`}</p>
        <p>{slow ? CHECKOUT_CONFIRM_WORDS.slow : CHECKOUT_CONFIRM_WORDS.confirming}</p>
        {slow && (
          <button type="button" className={`${shared.btn} ${shared.btnPrimary} ${css.pay}`} onClick={actions.done} {...part('done')}>
            Done
          </button>
        )}
      </div>
    );
  }

  const card = state.view === 'cardOnly' && state.phase === 'form';
  return (
    <div className={css.step} {...pieceRoot(contract)}>
      {state.notice && (
        <div className={css.notice} role="status" {...part('notice')}>
          <Icon name="info" small />
          <div>
            <b>{state.notice.title}</b>
            <span>{state.notice.body}</span>
          </div>
        </div>
      )}
      {card && (
        <div {...part('card')}>
          {/* The card waits for the number to call while the checkout asks for it (service-by-phone D5). */}
          <CardPaymentWait.Provider value={state.waitingForNumber}>
            <CardCheckoutPanel
              key={panel.key}
              ref={panel.ref}
              entry={panel.entry}
              intentRequest={panel.intentRequest}
              onCompleteChange={panel.onCompleteChange}
              onIntentReady={panel.onIntentReady}
              onSubmitInFlightChange={panel.onSubmitInFlightChange}
              onPaymentSubmitted={panel.onPaymentSubmitted}
              onDecline={panel.onDecline}
              onError={panel.onError}
              onConflict={panel.onConflict}
            />
          </CardPaymentWait.Provider>
        </div>
      )}
      {state.phase !== 'alreadyPaid' && (
        <p className={css.secure} {...part('secure')}>
          <Icon name="lock" small />
          Payments secured by Stripe
        </p>
      )}
      {state.phase === 'lapsed' ? (
        <div className={css.submit}>
          <button type="button" className={`${shared.btn} ${shared.btnPrimary} ${css.pay}`} onClick={actions.pickNewTime} {...part('pickNewTime')}>
            Pick a new time
          </button>
        </div>
      ) : (
        card && (
          <div className={css.submit}>
            {state.decline && (
              <p className={css.decline} role="alert" {...part('decline')}>
                <b>{state.decline}</b> {state.declineNote}
              </p>
            )}
            <button
              type="button"
              className={`${shared.btn} ${shared.btnPrimary} ${css.pay}`}
              disabled={!state.complete || state.paying || state.waitingForNumber}
              onClick={actions.pay}
              {...part('pay', (!state.complete || state.paying || state.waitingForNumber) && 'disabled')}
            >
              Confirm &amp; pay {state.amount}
            </button>
          </div>
        )
      )}
    </div>
  );
}
